import type React from 'react';
import {
  AlertTriangle,
  Cloud,
  DollarSign,
  FileCheck2,
  FileText,
  GitBranch,
  KeyRound,
  Route,
  UserRound,
} from 'lucide-react';

/**
 * Shared catalogue of "Live review views" — the one-click presets
 * surfaced both on the folder dashboard's Architecture-views tab and
 * (now) inside the canvas chrome's view-switcher dropdown.
 *
 * Keeping the catalogue in one place means the dashboard cards and
 * the in-canvas switcher can never disagree about what views exist
 * or how they should land on the explorer.
 */
export interface LiveReviewView {
  readonly id: string;
  readonly title: string;
  readonly description: string;
  /**
   * 'slice'   → focus preset filters the graph. Topology views like
   *             Attack Surface where the filtered subgraph IS the
   *             story.
   * 'overlay' → full graph stays visible, a lens tints resources and
   *             optional chips spotlight the offenders.
   */
  readonly kind: 'slice' | 'overlay';
  /** Used when kind === 'slice'. Maps to a FocusSpec preset id. */
  readonly presetId?: string;
  /** Used when kind === 'overlay'. Compass lens preset id. */
  readonly lensId?: string;
  /** Used when kind === 'overlay'. Quick-filter chip ids to pre-toggle. */
  readonly chips?: readonly string[];
  readonly zoom: 2 | 3 | 4 | 5;
  readonly panel: 'map' | 'scenarios';
  readonly icon: React.ElementType;
  readonly tone: 'red' | 'blue' | 'amber' | 'emerald' | 'slate';
}

export const LIVE_REVIEW_VIEWS: readonly LiveReviewView[] = [
  // ── Topology slices ───────────────────────────────────────────
  {
    id: 'attack-surface',
    title: 'Attack Surface',
    description: 'Public ingress, edge services, and reachable resources.',
    kind: 'slice',
    presetId: 'attack-surface',
    zoom: 3,
    panel: 'map',
    icon: AlertTriangle,
    tone: 'red',
  },
  {
    id: 'public-path-to-data',
    title: 'Public Path to Data',
    description: 'Internet entry points, hops, and candidate data stores.',
    kind: 'slice',
    presetId: 'public-path-to-data',
    zoom: 4,
    panel: 'map',
    icon: Route,
    tone: 'blue',
  },
  {
    id: 'identity-attack-paths',
    title: 'Identity Attack Paths',
    description:
      'Role trust, admin policy, wildcard permission, and principal-to-resource risk paths.',
    kind: 'slice',
    presetId: 'identity-attack-paths',
    zoom: 4,
    panel: 'map',
    icon: KeyRound,
    tone: 'red',
  },
  {
    id: 'planned-changes',
    title: 'Planned Changes',
    description: 'Terraform, CloudFormation, and proposed scenario deltas.',
    kind: 'slice',
    presetId: 'planned-changes',
    zoom: 4,
    panel: 'scenarios',
    icon: FileText,
    tone: 'slate',
  },

  // ── Lens overlays on the full estate ──────────────────────────
  {
    id: 'cost-waste',
    title: 'Cost Waste',
    description:
      'Full estate with cost heatmap and the cost / waste chips pre-applied.',
    kind: 'overlay',
    lensId: 'compass-lens-cost',
    chips: ['has-cost', 'cost-waste'],
    zoom: 5,
    panel: 'map',
    icon: DollarSign,
    tone: 'emerald',
  },
  {
    id: 'ownership-gaps',
    title: 'Ownership Gaps',
    description:
      'Full estate, "unowned" chip pre-applied — missing owner / team / cost-center signals dimmed against peers.',
    kind: 'overlay',
    lensId: 'compass-lens-team',
    chips: ['unowned'],
    zoom: 5,
    panel: 'map',
    icon: UserRound,
    tone: 'slate',
  },
  {
    id: 'observability-gaps',
    title: 'Observability Gaps',
    description:
      'Full estate with the findings chip on so resources missing alarms / logs stand out.',
    kind: 'overlay',
    lensId: 'compass-lens-exposure',
    chips: ['findings'],
    zoom: 5,
    panel: 'map',
    icon: Cloud,
    tone: 'amber',
  },
  {
    id: 'compliance-findings',
    title: 'Compliance Findings',
    description:
      'Full estate with the findings chip on — AWS Config, policy, and control findings highlighted against the rest of the resources.',
    kind: 'overlay',
    lensId: 'compass-lens-risk',
    chips: ['findings'],
    zoom: 5,
    panel: 'map',
    icon: FileCheck2,
    tone: 'blue',
  },
  {
    id: 'drift-from-baseline',
    title: 'Drift from Baseline',
    description: 'Full estate with the drift chip pre-applied.',
    kind: 'overlay',
    lensId: 'compass-lens-tier',
    chips: ['drift'],
    zoom: 5,
    panel: 'map',
    icon: GitBranch,
    tone: 'amber',
  },
];

export function findLiveReviewViewById(id: string | null | undefined): LiveReviewView | null {
  if (!id) return null;
  return LIVE_REVIEW_VIEWS.find((v) => v.id === id) ?? null;
}
